import React, { lazy, Suspense } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import NotFound from '../pages/NotFound';
import { ProtectedRoute } from '../components/auth/ProtectedRoute';
import PageLoadingFallback from '../components/feature/PageLoadingFallback';

// Lazy‑load page components
const HomePage = lazy(() => import('../pages/home/page'));
const SearchPage = lazy(() => import('../pages/search/page'));
const PsychologistProfilePage = lazy(() => import('../pages/psychologist-profile/page'));
const DashboardPage = lazy(() => import('../pages/dashboard/page'));
const PsychologistDashboardPage = lazy(() => import('../pages/psychologist-dashboard/page'));
const ForPsychologistsPage = lazy(() => import('../pages/for-psychologists/page'));
const CheckoutPage = lazy(() => import('../pages/checkout/page'));
const PaymentSuccessPage = lazy(() => import('../pages/payment-success/page'));
const CheckoutCancelledPage = lazy(() => import('../pages/checkout-cancelled/page'));
const WaitingRoomPage = lazy(() => import('../pages/waiting-room/page'));
const VideoRoomPage = lazy(() => import('../pages/video-room/page'));
const SessionFeedbackPage = lazy(() => import('../pages/session-feedback/page'));
const NotificationsPage = lazy(() => import('../pages/notifications/page'));
const ForPatientsPage = lazy(() => import('../pages/for-patients/page'));
const BlogPage = lazy(() => import('../pages/blog/page'));
const BlogArticlePage = lazy(() => import('../pages/blog/article/page'));
const PrivacidadePage = lazy(() => import('../pages/privacidade/page'));
const TermosPage = lazy(() => import('../pages/termos/page'));
const LoginPage = lazy(() => import('../pages/login/page'));
const RegisterPage = lazy(() => import('../pages/register/page'));
const ForgotPasswordPage = lazy(() => import('../pages/forgot-password/page'));
const MedicalRecordsPage = lazy(() => import('../pages/medical-records/page'));
const ProfileEditPage = lazy(() => import('../pages/profile-edit/page'));
const ScheduleConfigPage = lazy(() => import('../pages/schedule-config/page'));
const FinancialPage = lazy(() => import('../pages/financial/page'));
const TransactionDetailPage = lazy(() => import('../pages/transaction-detail/page'));
const AppointmentHistoryPage = lazy(() => import('../pages/appointment-history/page'));
const LocalIndexPage = lazy(() => import('../pages/local-index/page'));
const LocalCityPage = lazy(() => import('../pages/local/page'));
const PatientReportPage  = lazy(() => import('../pages/patient-report/page'));
const NovoPacientePage   = lazy(() => import('../pages/novo-paciente/page'));
const PatientDetailPage  = lazy(() => import('../pages/patient-detail/page'));
const SessionNotePage    = lazy(() => import('../pages/session-note/page'));
const NotificationSettingsPage = lazy(() => import('../pages/notification-settings/page'));
const SettingsPage = lazy(() => import('../pages/settings/page'));
const UpgradePage = lazy(() => import('../pages/upgrade/page'));
const ResetPasswordPage = lazy(() => import('../pages/reset-password/page'));
const AtualizarSenhaPage = lazy(() => import('../pages/atualizar-senha/page'));
const VerificarDoisFatoresPage = lazy(() => import('../pages/verificar-2fa/page'));
const SessionNotesPage = lazy(() => import('../pages/session-notes/page'));
const OnboardingPage = lazy(() => import('../pages/onboarding/page'));
const PricingPage = lazy(() => import('../pages/psychologist-dashboard/pricing-page'));
const SessionPage = lazy(() => import('../pages/session/page'));
const ClinicalIntelligencePage = lazy(() => import('../pages/clinical-intelligence/page'));
const IndicatorsPage = lazy(() => import('../pages/indicators/page'));
const SupportPage = lazy(() => import('../pages/support/page'));
const AdminDashboardPage = lazy(() => import('../pages/admin/dashboard/page'));
const AdminRepassesPage = lazy(() => import('../pages/admin/repasses/page'));
const ClinicaPage = lazy(() => import('../pages/clinica/page'));
const AcceptInvitePage = lazy(() => import('../pages/convite/page'));
const PatientAcceptInvitePage = lazy(() => import('../pages/aceitar-convite/page'));
const AutopilotPage = lazy(() => import('../pages/autopilot/page'));
const AutopilotApprovalsPage = lazy(() => import('../pages/autopilot-approvals/page'));
const PatientReceiptsPage = lazy(() => import('../pages/patient/receipts/PatientReceiptsPage'));
const PsychologistReceiptsPage = lazy(() => import('../pages/psychologist-dashboard/receipts-page'));
const ConfirmAppointmentPage = lazy(() => import('../pages/confirm-appointment/ConfirmAppointmentPage'));
const PublicPaymentPage = lazy(() => import('../pages/public-payment/page'));

const routes: RouteObject[] = [
  // ========== ROTAS PÚBLICAS ==========
  {
    path: '/pay/:code',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <PublicPaymentPage />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <HomePage />
      </Suspense>
    ),
  },
  {
    path: '/buscar-psicologos',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <SearchPage />
      </Suspense>
    ),
  },
  // Alias legado (EN) — redireciona para a URL canônica em PT.
  { path: '/search', element: <Navigate to="/buscar-psicologos" replace /> },
  {
    path: '/psychologist/:id',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <PsychologistProfilePage />
      </Suspense>
    ),
  },
  {
    path: '/confirmar-consulta/:appointmentId',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <ConfirmAppointmentPage />
      </Suspense>
    ),
  },
  {
    path: '/for-patients',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <ForPatientsPage />
      </Suspense>
    ),
  },
  {
    path: '/for-psychologists',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <ForPsychologistsPage />
      </Suspense>
    ),
  },
  {
    path: '/blog',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <BlogPage />
      </Suspense>
    ),
  },
  {
    path: '/blog/:slug',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <BlogArticlePage />
      </Suspense>
    ),
  },
  {
    path: '/privacidade',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <PrivacidadePage />
      </Suspense>
    ),
  },
  {
    path: '/termos',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <TermosPage />
      </Suspense>
    ),
  },
  // Aliases legados (EN) — redirecionam para as URLs canônicas em PT.
  { path: '/privacy', element: <Navigate to="/privacidade" replace /> },
  { path: '/terms',   element: <Navigate to="/termos"      replace /> },
  {
    path: '/terapia-online',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <LocalIndexPage />
      </Suspense>
    ),
  },
  {
    path: '/terapia-online/:citySlug',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <LocalCityPage />
      </Suspense>
    ),
  },
  {
    path: '/login',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: '/entrar',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: '/register',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <RegisterPage />
      </Suspense>
    ),
  },
  {
    path: '/cadastro',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <RegisterPage />
      </Suspense>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <ForgotPasswordPage />
      </Suspense>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <ResetPasswordPage />
      </Suspense>
    ),
  },
  {
    path: '/onboarding',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <OnboardingPage />
      </Suspense>
    ),
  },
  {
    // Tarefa #6 — troca obrigatória de senha fraca. requireAuth: só usuário
    // logado é forçado aqui pelo gate. O próprio ProtectedRoute não causa loop
    // (o gate exclui rotas que começam com /atualizar-senha).
    path: '/atualizar-senha',
    element: (
      <ProtectedRoute requireAuth>
        <Suspense fallback={<PageLoadingFallback />}>
          <AtualizarSenhaPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    // Desafio MFA (TOTP). SEM ProtectedRoute de propósito: o gate de MFA do
    // ProtectedRoute redireciona para cá, e a página valida sessão sozinha.
    path: '/verificar-2fa',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <VerificarDoisFatoresPage />
      </Suspense>
    ),
  },

  // ========== ROTAS PROTEGIDAS - PACIENTE ==========
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute requireAuth requireRole="patient">
        <Suspense fallback={<PageLoadingFallback />}>
          <DashboardPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/checkout',
    element: (
      <ProtectedRoute requireAuth requireRole="patient">
        <Suspense fallback={<PageLoadingFallback />}>
          <CheckoutPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/payment-success',
    element: (
      <ProtectedRoute requireAuth requireRole="patient">
        <Suspense fallback={<PageLoadingFallback />}>
          <PaymentSuccessPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/checkout-cancelled',
    element: (
      <ProtectedRoute requireAuth requireRole="patient">
        <Suspense fallback={<PageLoadingFallback />}>
          <CheckoutCancelledPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/meus-reembolsos',
    element: (
      <ProtectedRoute requireAuth requireRole="patient">
        <Suspense fallback={<PageLoadingFallback />}>
          <PatientReceiptsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/appointment-history',
    element: (
      <ProtectedRoute requireAuth requireRole="patient">
        <Suspense fallback={<PageLoadingFallback />}>
          <AppointmentHistoryPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/session-feedback',
    element: (
      <ProtectedRoute requireAuth requireRole="patient">
        <Suspense fallback={<PageLoadingFallback />}>
          <SessionFeedbackPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/waiting-room/:appointmentId',
    element: (
      <ProtectedRoute requireAuth requireRole="patient">
        <Suspense fallback={<PageLoadingFallback />}>
          <WaitingRoomPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/video-room',
    element: (
      <ProtectedRoute requireAuth requireRole="patient">
        <Suspense fallback={<PageLoadingFallback />}>
          <VideoRoomPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // ========== ROTAS PROTEGIDAS - PSICÓLOGO ==========
  {
    path: '/psychologist-dashboard',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <PsychologistDashboardPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/psychologist-onboarding',
    element: <Navigate to="/onboarding" replace />,
  },
  {
    path: '/schedule-config',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <ScheduleConfigPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/medical-records/:patientId',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <MedicalRecordsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/session-notes',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <SessionNotesPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/indicators',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <IndicatorsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/autopilot',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <AutopilotPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/autopilot/aprovacoes',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <AutopilotApprovalsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/clinical-intelligence',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <ClinicalIntelligencePage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/clinical-copilot',
    element: <Navigate to="/clinical-intelligence" replace />,
  },
  {
    path: '/patient-report',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <PatientReportPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/pacientes/novo',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <NovoPacientePage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/pacientes/:id',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <PatientDetailPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/prontuario/:id',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <SessionNotePage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/financial',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <FinancialPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/recibos-tuss',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <PsychologistReceiptsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/transaction/:txId',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <TransactionDetailPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/psychologist-dashboard/pricing',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <PricingPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/upgrade',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <UpgradePage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/clinica',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <ClinicaPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // ========== ROTAS PÚBLICAS - CONVITE ==========
  {
    path: '/convite/:token',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <AcceptInvitePage />
      </Suspense>
    ),
  },
  {
    // Convite de paciente (psicóloga → paciente). Token via query string.
    path: '/aceitar-convite',
    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <PatientAcceptInvitePage />
      </Suspense>
    ),
  },

  // ========== ROTAS PROTEGIDAS - COMPARTILHADAS (AMBOS OS ROLES + ADMIN) ==========
  {
    path: '/admin/dashboard',
    element: (
      <ProtectedRoute requireAuth requireRole="admin">
        <Suspense fallback={<PageLoadingFallback />}>
          <AdminDashboardPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/repasses',
    element: (
      <ProtectedRoute requireAuth requireRole="admin">
        <Suspense fallback={<PageLoadingFallback />}>
          <AdminRepassesPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/session/:id',
    element: (
      <ProtectedRoute requireAuth>
        <Suspense fallback={<PageLoadingFallback />}>
          <SessionPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/profile-edit',
    element: (
      <ProtectedRoute requireAuth>
        <Suspense fallback={<PageLoadingFallback />}>
          <ProfileEditPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/notifications',
    element: (
      <ProtectedRoute requireAuth>
        <Suspense fallback={<PageLoadingFallback />}>
          <NotificationsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/notification-settings',
    element: (
      <ProtectedRoute requireAuth>
        <Suspense fallback={<PageLoadingFallback />}>
          <NotificationSettingsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings',
    element: (
      <ProtectedRoute requireAuth requireRole="psychologist">
        <Suspense fallback={<PageLoadingFallback />}>
          <SettingsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/support',
    element: (
      <ProtectedRoute requireAuth>
        <Suspense fallback={<PageLoadingFallback />}>
          <SupportPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // ========== 404 ==========
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;
